import { useState } from "react";
import { Sheet } from "./ui";

/**
 * "Video on YouTube? See how to download it" beside the questionnaire's file hint (pages/Beta.tsx, while no
 * file is chosen), and the sheet it opens: an 8-second screen recording of YouTube Studio (Content, a video's
 * three-dot Options button, then Download; its channel and team names are replaced) above the same four steps
 * in words. A bottom sheet on a phone, a 960 px dialog on a desktop. Design: the claude.ai canvas copied to
 * docs/design/mocks/beta-youtube-help/ (owner, 2026-11-03).
 *
 * The facts come from YouTube's help (support.google.com/youtube/answer/56100, read 2026-11-03): the download
 * is an MP4 at 720p or 360p depending on the video's size, and only from a channel the person manages. The
 * steps are for YouTube Studio on a computer, so the phone sheet says so and links nowhere.
 */
export const STUDIO_URL = "https://studio.youtube.com";
/** The recording, served from apps/web/public (1296 x 648, 200 frames at 25 fps, 151 KB). */
export const STUDIO_GIF = "/help/youtube-studio-download.gif";

export function YoutubeHelp({ desktop }: { desktop: boolean }) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const studio = desktop
    ? <a className="u" href={STUDIO_URL} target="_blank" rel="noopener noreferrer">studio.youtube.com</a>
    : <b className="med">studio.youtube.com</b>;
  return (
    <>
      <button type="button" className="u bq-yt-link" aria-haspopup="dialog" onClick={() => setOpen(true)} data-testid="bq-yt-help">
        <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="3" width="13" height="10" rx="2.5" /><path d="M6.75 6.1v3.8L9.95 8z" /></svg>
        Video on YouTube? See how to download it
      </button>
      {open && (
        <Sheet onClose={close} className="yt-help">
          <div data-testid="yt-help">
            <div className="yt-help-head">
              <h2>Get your video from YouTube</h2>
              <button type="button" className="yt-help-x" aria-label="Close" onClick={close} data-testid="yt-help-x">
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" /></svg>
              </button>
            </div>
            <p className="sub mt6">
              {desktop
                ? "If your match is on a YouTube channel you manage, download it from YouTube Studio, then choose the file under Your video."
                : "These steps are for YouTube Studio on a computer. Download the match there, then choose the file under Your video."}
            </p>
            <img className="yt-help-gif" src={STUDIO_GIF} width={1296} height={648} data-testid="yt-help-gif"
              alt="Screen recording of YouTube Studio: the Content page, the three-dot Options button on a video, then Download in its menu" />
            <ol className="yt-help-steps" data-testid="yt-help-steps">
              <li><span className="bq-n" aria-hidden="true">1</span><span>Open {studio} and sign in.</span></li>
              <li><span className="bq-n" aria-hidden="true">2</span><span>Choose <b className="med">Content</b> in the menu on the left.</span></li>
              <li><span className="bq-n" aria-hidden="true">3</span><span>Point at your match, then click the three dots, <b className="med">Options</b>.</span></li>
              <li><span className="bq-n" aria-hidden="true">4</span><span>Click <b className="med">Download</b>. YouTube saves an MP4 file.</span></li>
            </ol>
            <p className="tiny mt16">YouTube downloads it at 720p or 360p, depending on the video’s size. Only someone who manages the channel can download its videos.</p>
            <div className="yt-help-actions">
              {desktop ? (
                <>
                  <button type="button" className="btn ghost" onClick={close}>Got it</button>
                  <a className="btn" href={STUDIO_URL} target="_blank" rel="noopener noreferrer" data-testid="yt-help-studio">
                    Open YouTube Studio
                    <svg viewBox="0 0 14 14" aria-hidden="true"><path d="M8 2h4v4M12 2L6.5 7.5M10.5 8.5v3a.5.5 0 0 1-.5.5H2.5a.5.5 0 0 1-.5-.5V4a.5.5 0 0 1 .5-.5h3" /></svg>
                  </a>
                </>
              ) : (
                <button type="button" className="btn" onClick={close}>Got it</button>
              )}
            </div>
          </div>
        </Sheet>
      )}
    </>
  );
}
